import { ReactNode, useEffect, useRef, useState } from "react";
import GooglePlacesAutocomplete from "../GooglePlacesAutocomplete";
// @ts-ignore
import { components } from "react-select";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { toIcon, poweredByGoogleIcon } from "../../common/vector";
import { checkIfStartsWithGivenString } from "../../common/utils/helpers/checkIfStartsWithGivenString";
import { getLengthOfStringBeforeGivenWordWithoutSpaces } from "../../common/utils/helpers/getLengthOfStringBeforeGivenWordWithoutSpaces";

const radiusInMeters = 30000;

export interface GooglePlaceValue {
  value: {
    description?: string;
    place_id?: string;
    structured_formatting?: {
      main_text?: string;
      secondary_text?: string;
    };
  };
}

interface SingleGooglePlacesInputProps {
  value: GooglePlaceValue | null;
  onChange: (value: GooglePlaceValue | null, action: { action: string }) => void;
  placeholder: string;
  apiKey: string;
  location?: { lat: number; lng: number };
  inputTestId?: string;
  dataCyAttribute?: string;
  onClear?: () => void;
  disabled?: boolean;
  leftIcon?: ReactNode;
}

export default function SingleGooglePlacesInput({
  value,
  onChange,
  placeholder,
  apiKey,
  location = { lat: 0, lng: 0 },
  inputTestId,
  dataCyAttribute,
  onClear,
  disabled = false,
  leftIcon
}: SingleGooglePlacesInputProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const backgroundColorDefault = "transparent";

  const [onInputClick, setOnInputClick] = useState(false);
  const [borderWidth, setBorderWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (elementRef.current) {
      setBorderWidth(parseInt(getComputedStyle(elementRef.current)?.borderInlineWidth || "0", 10));
      setContainerWidth(parseInt(getComputedStyle(elementRef.current)?.width || "0", 10) || 0);
    }
  }, []);

  const SingleValueInput = ({ children, ...props }: any) => {
    const mainText = props?.data?.value?.structured_formatting?.main_text;
    const secondaryText = props?.data?.value?.structured_formatting?.secondary_text;
    return (
      <components.SingleValue {...props}>
        <>
          <div className="main-text">{mainText ?? "/"}</div>
          <div className="secondary-text">{secondaryText ?? "/"}</div>
        </>
      </components.SingleValue>
    );
  };

  const PlaceholderInput = (props: any) => (
    <>
      {props?.value || (onInputClick && props.isFocused) ? null : (
        <components.Placeholder {...props}>
          <div style={{ marginTop: "12px", color: "#9c9c9c", fontSize: "15px" }}>{placeholder}</div>
        </components.Placeholder>
      )}
    </>
  );

  const ClearIndicatorInput = (props: any) => {
    const handleClearInput = () => {
      props.clearValue();
      setOnInputClick(false);
      onClear?.();
    };
    return (
      <div
        onClick={handleClearInput}
        onTouchEnd={handleClearInput}
        style={{ display: disabled ? "none" : "block" }}
        data-cy={dataCyAttribute ? `${dataCyAttribute}-clear` : ""}
      >
        <div className="remove-x">X</div>
      </div>
    );
  };

  const MenuList = (props: any) => (
    <div
      className="menu-list"
      data-cy={dataCyAttribute ? `${dataCyAttribute}-menu` : ""}
      style={{
        width: containerWidth || "100%",
        left: borderWidth ? `-${borderWidth}px` : 0,
        zIndex: 1000,
        position: "relative",
        opacity: 1,
        display: props?.selectProps?.inputValue ? "block" : "none"
      }}
    >
      {props?.selectProps?.inputValue !== "" ? (
        props.children
      ) : (
        <div
          style={{
            boxSizing: "border-box",
            color: "hsl(0, 0%, 60%)",
            padding: "8px 12px",
            textAlign: "left",
            zIndex: 1000
          }}
        >
          {t("googleSearch.typeToSearch")}
        </div>
      )}
      <div style={{ position: "relative", paddingBottom: "30px", zIndex: 1000 }}>
        <img
          style={{
            position: "absolute",
            bottom: "10px",
            left: "15px",
            height: "10px",
            width: "auto",
            zIndex: 1000
          }}
          src={poweredByGoogleIcon?.default}
          alt="google"
        />
      </div>
    </div>
  );

  const Options = (props: any) => {
    const lengthOfInputValue = props?.selectProps.inputValue?.replace(/\s/g, "").split("")?.length;
    const inputValueSplitOnWordsArr = props?.selectProps?.inputValue?.split(" ") ?? [];
    const mainText = props?.data?.value?.structured_formatting?.main_text ?? "";
    const placeId = props?.data?.value?.place_id;
    const optionKey = placeId || mainText || "option";
    return (
      <components.Option
        {...props}
        className="option"
        data-cy={dataCyAttribute ? `${dataCyAttribute}-option-${optionKey}` : ""}
      >
        <div className="main-text">
          {mainText.split(" ").map((word: string, index: number) => (
            <span key={index}>
              {index > 0 && <span> </span>}
              {word.split("").map((letter: string, letterIndex: number) => {
                const highlighted =
                  (letterIndex <
                    (inputValueSplitOnWordsArr.find((w: string) => checkIfStartsWithGivenString(word, w))?.length ??
                      0) &&
                    inputValueSplitOnWordsArr.find((w: string) => checkIfStartsWithGivenString(word, w))) ||
                  (letterIndex <
                    lengthOfInputValue -
                      (index > 0 ? getLengthOfStringBeforeGivenWordWithoutSpaces(mainText, index) : 0) &&
                    checkIfStartsWithGivenString(mainText, props?.selectProps?.inputValue)) ||
                  (letterIndex < word?.split("").length &&
                    checkIfStartsWithGivenString(props?.selectProps?.inputValue, mainText));
                return (
                  <span key={letterIndex} className={highlighted ? "main-text--bold" : ""}>
                    {letter}
                  </span>
                );
              })}
            </span>
          ))}
        </div>
        <div className="secondary-text">{props.data?.value?.structured_formatting?.secondary_text}</div>
      </components.Option>
    );
  };

  const sharedStyles = {
    menu: (defaultStyles: any) => ({ ...defaultStyles }),
    indicatorSeparator: (defaultStyles: any) => ({ ...defaultStyles, display: "none" }),
    dropdownIndicator: (defaultStyles: any) => ({ ...defaultStyles, display: "none" }),
    popupIndicator: (defaultStyles: any) => ({
      ...defaultStyles,
      backgroundImage: `url(${poweredByGoogleIcon?.default}) !important`,
      width: "20px !important",
      height: "20px !important"
    }),
    clearIndicator: (defaultStyles: any) => ({
      ...defaultStyles,
      color: theme.palette.primary.light,
      marginRight: 4,
      padding: "8px",
      cursor: "pointer",
      "&:hover": {
        color: theme.palette.primary.light,
        backgroundColor: theme.palette.primary.main,
        borderRadius: "50%"
      }
    }),
    placeholder: (base: any) => ({ ...base, margin: 0, padding: 0 }),
    container: (base: any) => ({ ...base, flex: 1 }),
    singleValue: (base: any) => ({ ...base, margin: 0, minHeight: "30px" }),
    control: (base: any) => ({
      ...base,
      paddingLeft: "30px",
      flex: 1,
      backgroundColor: backgroundColorDefault,
      border: "none",
      outline: "none",
      width: "100%",
      borderRadius: 0,
      boxShadow: "none",
      cursor: disabled ? "default" : "text",
      "&:hover": { border: "none" }
    }),
    option: (styles: any, { isFocused }: any) => ({
      ...styles,
      backgroundColor: isFocused ? theme.palette.grayLight.main : "transparent",
      color: "black",
      "&:active": { background: `${theme.palette.grayLight.main} !important` }
    }),
    input: () => ({
      fontFamily: "Neutraface2Book",
      minHeight: "38px",
      lineHeight: "38px",
      "& input": { font: "inherit" }
    }),
    noOptionsMessage: (base: any) => ({ ...base, textAlign: "left" }),
    loadingMessage: (base: any) => ({ ...base, textAlign: "left" })
  };

  return (
    <div
      ref={elementRef}
      className={`custom-google-places custom-google-places--single ${
        disabled ? "custom-google-places--disabled-single" : ""
      }`}
      data-cy={dataCyAttribute || ""}
    >
      <div className="custom-google-places__input-box" style={{ zIndex: 2 }}>
        {leftIcon ? (
          <span className="custom-google-places__icon-node">{leftIcon}</span>
        ) : (
          <img className="custom-google-places__icon" src={toIcon?.default} alt="destination" />
        )}
        <GooglePlacesAutocomplete
          apiKey={apiKey}
          selectProps={{
            isClearable: true,
            getOptionLabel: (options: any) => options?.value?.structured_formatting?.main_text ?? "",
            inputId: inputTestId,
            value: value as any,
            onChange: (address: GooglePlaceValue | null, action: { action: string }) => {
              if (action.action === "clear" && onClear) onClear();
              onChange(address, action);
            },
            onMenuOpen: () => {
              setOnInputClick(true);
              if (elementRef.current) {
                setBorderWidth(parseInt(getComputedStyle(elementRef.current)?.borderInlineWidth || "0", 10));
                setContainerWidth(parseInt(getComputedStyle(elementRef.current)?.width || "0", 10) || 0);
              }
            },
            onBlur: () => setOnInputClick(false),
            isSearchable: !value,
            openMenuOnClick: !value,
            isDisabled: disabled,
            classNamePrefix: "react-select",
            styles: sharedStyles,
            components: {
              Placeholder: PlaceholderInput,
              SingleValue: SingleValueInput,
              MenuList,
              Option: Options,
              ClearIndicator: ClearIndicatorInput
            }
          }}
          autocompletionRequest={{
            radius: radiusInMeters,
            location
          }}
        />
      </div>
    </div>
  );
}
